/**
 * Navigation route grouping — 5 collapsible groups for sidebar organization.
 *
 * Routes are organized into 5 groups, with a fallback for ungrouped routes.
 * Groups can be collapsed/expanded and remember their state in localStorage.
 *
 * doc 147 §5.2 (Phase 1 UI).
 */

import type { Route } from './hashRouter.js';

export type NavGroupName = 'exploration' | 'intelligence' | 'operations' | 'compliance' | 'settings';

interface NavGroupConfig {
  name: NavGroupName;
  label: string;
  icon: string;
  routes: Array<Route['kind']>;
}

/**
 * 5 collapsible route groups for sidebar navigation.
 * Routes not listed here are not shown in the sidebar groups (e.g., welcome, entity-detail).
 */
export const NAV_GROUPS: NavGroupConfig[] = [
  {
    name: 'exploration',
    label: 'Exploration',
    icon: 'explore',
    routes: [
      'constellation',
      'relationship-map',
      'domain-map',
      'catalog',
      'catalog-graph',
      'flag-catalog',
      'lineage',
      'user-journeys',
      'hub',
      'statemachine',
    ],
  },
  {
    name: 'intelligence',
    label: 'Intelligence',
    icon: 'auto_awesome',
    routes: ['ask', 'patterns', 'behavioral', 'heatmap', 'storyboard', 'trace', 'event-flow'],
  },
  {
    name: 'operations',
    label: 'Operations',
    icon: 'build',
    routes: [
      'lint',
      'simulate',
      'testdata',
      'sandbox',
      'new-design-change',
      'diff',
      'drift',
      'workspace',
      'boards',
    ],
  },
  {
    name: 'compliance',
    label: 'Compliance',
    icon: 'verified_user',
    routes: ['compliance', 'compliance-hq', 'pii-tracer'],
  },
  {
    name: 'settings',
    label: 'Settings',
    icon: 'settings',
    routes: ['settings', 'live-monitor', 'dashboard'],
  },
];

/**
 * Return the nav group name for a given route kind, or null if not in any group.
 *
 * Routes not listed in NAV_GROUPS (e.g., welcome, entity-detail, refactor) return null.
 */
export function getGroupForRoute(routeKind: Route['kind']): NavGroupName | null {
  for (const group of NAV_GROUPS) {
    if ((group.routes as Array<Route['kind']>).includes(routeKind)) {
      return group.name;
    }
  }
  return null;
}

/**
 * Return the nav group config for a given group name.
 */
export function getNavGroup(groupName: NavGroupName): NavGroupConfig | undefined {
  return NAV_GROUPS.find((g) => g.name === groupName);
}

/**
 * localStorage key for a collapsed-state persistence.
 *
 * Versioned (`v2`) because the default flipped from expanded to collapsed —
 * an absent key meant "expanded" under the old scheme and means "collapsed"
 * under this one, so reusing the old key would silently reinterpret existing
 * users' untouched/explicitly-expanded groups as collapsed. The version bump
 * makes every user (old or new) start clean under the new default instead.
 */
export function getGroupStorageKey(groupName: NavGroupName): string {
  return `atlas-nav-group-v2-${groupName}`;
}

/**
 * Get the collapsed state for a nav group (defaults to true/collapsed).
 * Only expands if explicitly stored as 'expanded'.
 */
export function isGroupCollapsed(groupName: NavGroupName): boolean {
  try {
    const key = getGroupStorageKey(groupName);
    const stored = localStorage.getItem(key);
    return stored !== 'expanded';
  } catch {
    return true; // Default to collapsed if storage unavailable
  }
}

/**
 * Set the collapsed state for a nav group.
 */
export function setGroupCollapsed(groupName: NavGroupName, collapsed: boolean): void {
  try {
    const key = getGroupStorageKey(groupName);
    if (collapsed) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, 'expanded');
    }
  } catch {
    // Ignore storage errors (private browsing, quota exhausted)
  }
}
